
import { ChevronDown, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollapsibleTrigger } from "@/components/ui/collapsible";
import { motion } from "framer-motion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useCustomExerciseName } from "@/hooks/useCustomNames";
import { EditNameDialog } from "../EditNameDialog";

interface ExerciseHeaderProps {
  id: string;
  name: string;
  isOpen: boolean;
  onEdit: () => void;
  onDelete: () => void;
  showControls?: boolean;
}

export const ExerciseHeader = ({
  id,
  name,
  isOpen,
  onEdit,
  onDelete,
  showControls = true
}: ExerciseHeaderProps) => {
  const { customName, updateExerciseName } = useCustomExerciseName(id);

  return (
    <div className="flex items-center justify-between gap-2">
      <CollapsibleTrigger asChild>
        <button className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity">
          <span className="text-base font-medium text-primary/90 forest:text-white dark:text-white">
            {customName || name}
          </span>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
              duration: 0.3
            }}
          >
            <ChevronDown className="h-4 w-4 text-primary dark:text-white forest:text-white dark:text-white" />
          </motion.div>
        </button>
      </CollapsibleTrigger>

      {showControls && (
        <motion.div 
          className="flex items-center gap-2"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 25,
            duration: 0.2
          }}
        >
          <EditNameDialog
            currentName={customName || name}
            onSave={updateExerciseName}
            title="Editar nombre del ejercicio"
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 sm:h-8 sm:w-8  dark:bg-transparent forest:bg-forest/50 sunset:bg-black/30"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary dark:text-white ocean:text-black forest:text-white ocean:hover:bg-black sunset:text-white" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción eliminará el ejercicio de la rutina.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-clubbers-red hover:bg-clubbers-red/90"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </motion.div>
      )}
    </div>
  );
};
